import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProjectFormData } from '../schemas/projectFormSchema';
import Checkbox from '@/components/ui/Checkbox/Checkbox';

/**
 * Step 3 of 3 — free-text notes and the legal confirmation. (The project's name used to be
 * here; it moved to step 1.)
 * The consent text holds a link, so the checkbox is named via aria-labelledby rather than
 * wrapped in a <label> (which would hijack the link's click).
 */
const ProjectSubmissionStep = () => {
  const { t } = useTranslation();
  const { register, watch, setValue, formState: { errors } } = useFormContext<ProjectFormData>();

  const legalTermsApproved = watch('legalTermsApproved') ?? false;

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h4 className="body-lr-semibold text-(--font-primary)">
          {t('projects.wizard.step3.anythingElseTitle')}
        </h4>
        <label htmlFor="project-user-comments" className="body-md-semibold text-(--font-secondary)">
          {t('projects.wizard.step3.commentsLabel')}
        </label>
        <textarea
          id="project-user-comments"
          rows={6}
          className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular resize-none"
          placeholder={t('projects.wizard.step3.commentsPlaceholder')}
          {...register('userComments')}
        />
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-3 bg-(--surface-secondary) rounded-lg p-4">
          <Checkbox
            checked={legalTermsApproved}
            onCheckedChange={(checked) => setValue('legalTermsApproved', checked, { shouldValidate: true })}
            ariaLabelledBy="project-legal-terms-text"
            ariaInvalid={!!errors.legalTermsApproved}
            ariaRequired
          />
          <span id="project-legal-terms-text" className="body-sm-regular">
            {t('projects.wizard.step3.legalPrefix')}{' '}
            <a href="/terms" target="_blank" rel="noreferrer" className="underline">
              {t('projects.wizard.step3.legalLink')}
            </a>{' '}
            {t('projects.wizard.step3.legalSuffix')}
          </span>
        </div>
        {errors.legalTermsApproved && (
          <p className="text-red-500 body-sm-regular">{errors.legalTermsApproved.message}</p>
        )}
      </div>
    </div>
  );
};

export default ProjectSubmissionStep;
